import { useState } from "react";
import { useLocation, useParams } from "wouter";
import { CheckCircle2, Send, Upload, XCircle } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import {
  useExpediente,
  useCandidate,
  useElementsForStandard,
  usePracticalScoresForExpediente,
  usePracticalNotesForExpediente,
  usePrechecklistForExpediente,
  useEffectiveUser,
  useUsersByRole,
} from "@/store/hooks";

export default function PracticalEvaluation() {
  const params = useParams<{ expedienteId: string }>();
  const expedienteId = params.expedienteId ?? "";
  const [, setLocation] = useLocation();

  const expediente = useExpediente(expedienteId);
  const candidate = useCandidate(expediente?.candidatoId);
  const elements = useElementsForStandard(expediente?.estandar);
  const scores = usePracticalScoresForExpediente(expedienteId);
  const notes = usePracticalNotesForExpediente(expedienteId);
  const pre = usePrechecklistForExpediente(expedienteId);

  const me = useEffectiveUser();
  const evaluators = useUsersByRole("evaluador");
  const evalId = (me?.roles.includes("evaluador") ? me.id : evaluators[0]?.id) ?? "evaluador-anon";

  const setCriterion = useDemoStore((s) => s.setPracticalCriterion);
  const setNotes = useDemoStore((s) => s.setPracticalNotes);
  const submit = useDemoStore((s) => s.submitPracticalEvaluation);

  const [submitMsg, setSubmitMsg] = useState<string | null>(null);

  if (!expediente) {
    return (
      <AppLayout breadcrumb={["Evaluador", "Expediente", "Práctica en circuito"]}>
        <Card><div style={{ padding: 20, textAlign: "center" }}>Expediente no encontrado.</div></Card>
      </AppLayout>
    );
  }

  const allCrits = elements.flatMap((el) => el.criterios.map((c) => ({ ...c, elementId: el.id, elementCodigo: el.codigo, elementTitulo: el.titulo })));
  const evaluados = allCrits.filter((c) => scores[c.id]).length;
  const competentes = allCrits.filter((c) => scores[c.id]?.resultado === "competente").length;
  const faltantes = allCrits.length - evaluados;
  const yaEnviada = expediente.evaluacionPractica.aplicada;

  const handleSubmit = () => {
    const res = submit(expedienteId, evalId);
    if (!res.ok) {
      setSubmitMsg(`Faltan ${res.missing} criterios por evaluar.`);
      return;
    }
    setSubmitMsg(null);
    setLocation(`/dictaminador/expediente/${expedienteId}`);
  };

  return (
    <AppLayout breadcrumb={["Evaluador", `Expediente ${expedienteId}`, "Práctica en circuito"]}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: candidate?.avatarColor, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>{candidate?.iniciales}</div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--cc-navy)" }}>{candidate?.nombre}</div>
            <div style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>{expediente.estandar} · {allCrits.length} criterios · {elements.length} elementos</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {pre?.completedAt ? <Badge tone="green">Preop. aplicada</Badge> : <Badge tone="amber">Sin preop.</Badge>}
          {yaEnviada && <Badge tone="teal">Enviada a dictamen</Badge>}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {elements.length === 0 ? (
            <Card>
              <SectionTitle>Sin elementos prácticos</SectionTitle>
              <div style={{ fontSize: 13, color: "var(--cc-gray-600)" }}>
                El estándar {expediente.estandar} no tiene elementos configurados. Pide al administrador que cree los elementos y criterios de evaluación.
              </div>
            </Card>
          ) : (
            elements.map((el) => (
              <Card key={el.id} padding={0}>
                <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--cc-gray-200)" }}>
                  <SectionTitle>{el.codigo} · {el.titulo}</SectionTitle>
                </div>
                {el.criterios.length === 0 && (
                  <div style={{ padding: 16, fontSize: 12, color: "var(--cc-gray-500)" }}>Sin criterios definidos.</div>
                )}
                {el.criterios.map((c) => {
                  const sc = scores[c.id];
                  return (
                    <div key={c.id} style={{ padding: "12px 18px", borderTop: "1px solid var(--cc-gray-100)" }} data-testid={`criterio-${c.codigo}`}>
                      <div style={{ display: "grid", gridTemplateColumns: "60px 1fr auto", gap: 12, alignItems: "center" }}>
                        <div style={{ fontWeight: 700, color: "var(--cc-navy)" }}>{c.codigo}</div>
                        <div>
                          <div style={{ fontSize: 13, color: "var(--cc-gray-800)" }}>{c.descripcion}</div>
                          <div style={{ fontSize: 11, color: "var(--cc-gray-500)", marginTop: 2 }}>Tipo: {c.tipo} · Ponderación {c.ponderacion}%</div>
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            onClick={() => setCriterion(expedienteId, c.id, { resultado: "competente", evaluadorId: evalId })}
                            data-testid={`button-comp-${c.codigo}`}
                            style={{ padding: "6px 10px", borderRadius: 6, border: "none", cursor: "pointer", background: sc?.resultado === "competente" ? "var(--cc-green)" : "var(--cc-green-bg)", color: sc?.resultado === "competente" ? "white" : "var(--cc-green)", fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}
                          >
                            <CheckCircle2 size={12} /> Competente
                          </button>
                          <button
                            onClick={() => setCriterion(expedienteId, c.id, { resultado: "todavia-no", evaluadorId: evalId })}
                            data-testid={`button-no-${c.codigo}`}
                            style={{ padding: "6px 10px", borderRadius: 6, border: "none", cursor: "pointer", background: sc?.resultado === "todavia-no" ? "var(--cc-amber)" : "var(--cc-amber-bg)", color: sc?.resultado === "todavia-no" ? "white" : "var(--cc-amber)", fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}
                          >
                            <XCircle size={12} /> Todavía no
                          </button>
                        </div>
                      </div>
                      {sc && (
                        <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                          <input
                            placeholder="Nota del evaluador (opcional)"
                            value={sc.nota ?? ""}
                            onChange={(e) => setCriterion(expedienteId, c.id, { nota: e.target.value })}
                            data-testid={`note-${c.codigo}`}
                            style={inputStyle}
                          />
                          <div style={{ display: "flex", gap: 4 }}>
                            <input
                              placeholder="URL de evidencia (foto/video)"
                              value={sc.evidenciaUrl ?? ""}
                              onChange={(e) => setCriterion(expedienteId, c.id, { evidenciaUrl: e.target.value })}
                              data-testid={`evidence-${c.codigo}`}
                              style={inputStyle}
                            />
                            <button
                              onClick={() => setCriterion(expedienteId, c.id, { evidenciaUrl: `https://evidencias.demo/${expedienteId}/${c.codigo}.mp4` })}
                              data-testid={`simulate-evidence-${c.codigo}`}
                              title="Simular subida"
                              style={{ padding: "4px 8px", border: "1px solid var(--cc-gray-200)", borderRadius: 6, background: "white", cursor: "pointer", color: "var(--cc-teal)" }}
                            >
                              <Upload size={13} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </Card>
            ))
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card>
            <SectionTitle>Progreso</SectionTitle>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13, color: "var(--cc-gray-700)" }}>
              <div>Evaluados: <strong>{evaluados}</strong> / {allCrits.length}</div>
              <div>Competente: <strong style={{ color: "var(--cc-green)" }}>{competentes}</strong></div>
              <div>Todavía no: <strong style={{ color: "var(--cc-amber)" }}>{evaluados - competentes}</strong></div>
              <div style={{ height: 6, background: "var(--cc-gray-100)", borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: allCrits.length ? `${(evaluados / allCrits.length) * 100}%` : "0%", height: "100%", background: "var(--cc-teal)" }} />
              </div>
            </div>
          </Card>

          <Card>
            <SectionTitle>Observaciones del evaluador</SectionTitle>
            <textarea
              value={notes}
              onChange={(e) => setNotes(expedienteId, e.target.value)}
              placeholder="Anote conductas observadas, condiciones del circuito, sugerencias..."
              data-testid="textarea-notes"
              style={{ width: "100%", minHeight: 110, padding: 10, border: "1px solid var(--cc-gray-200)", borderRadius: 8, fontSize: 13, fontFamily: "inherit", resize: "vertical" }}
            />
          </Card>

          {submitMsg && (
            <div style={{ padding: "10px 14px", background: "var(--cc-amber-bg)", color: "var(--cc-amber)", borderRadius: 8, fontSize: 12, fontWeight: 600 }} data-testid="submit-warning">
              {submitMsg}
            </div>
          )}

          <PrimaryButton onClick={handleSubmit} testId="button-submit-evaluation" disabled={faltantes > 0}>
            <Send size={13} /> Enviar a dictaminador{faltantes > 0 ? ` (faltan ${faltantes})` : ""}
          </PrimaryButton>
          <PrimaryButton variant="ghost" onClick={() => setLocation("/evaluador")} testId="button-back-agenda">
            ← Guardar y volver
          </PrimaryButton>
        </div>
      </div>
    </AppLayout>
  );
}

const inputStyle: React.CSSProperties = {
  flex: 1, width: "100%",
  padding: "6px 8px",
  border: "1px solid var(--cc-gray-200)",
  borderRadius: 6,
  fontSize: 12,
  fontFamily: "inherit",
};
